import React from 'react';
import { LAB_PANEL_TIERS, getLabPanelTier, getCategoriesForSex } from '../../data/labPanelData';

// §lab-panel-tiers — /lab-panel overview: pick the 60 / 125 / 160-biomarker
// panel, then see what it includes. The chosen tier id lives in
// answers['panel-overview'].
const LabPanelOverview = ({ question, onNext, sex, tierId, onSelectTier }) => {
  const tier = getLabPanelTier(tierId);
  return (
    <div data-testid="lab-panel-overview">
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-1">
        {question.question}
      </h2>
      {question.subtitle && <p className="text-center text-gray-500 mb-4">{question.subtitle}</p>}
      <div className="grid grid-cols-3 gap-2 mb-5" role="radiogroup" aria-label="Blood panel">
        {LAB_PANEL_TIERS.map((t) => {
          const selected = t.id === tier.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`lab-panel-tier-${t.id}`}
              onClick={() => onSelectTier(t.id)}
              className={`rounded-xl border-2 px-2 py-3 text-center transition-colors ${
                selected ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t.tierLabel}</span>
              <span className="block text-lg font-bold text-gray-900 leading-tight">{t.biomarkers}+</span>
              <span className="block text-xs text-gray-500">biomarkers</span>
              <span className="block mt-1 font-semibold text-[#2d8a5f]">${t.price}</span>
            </button>
          );
        })}
      </div>
      <p className="text-center font-semibold text-gray-900">{tier.name}</p>
      <div className="flex items-baseline justify-center gap-2 mb-4">
        <span className="text-4xl font-bold text-[#2d8a5f]">${tier.price}</span>
        <span className="text-gray-500">one-time · results in days</span>
      </div>
      {tier.highlights ? (
        <div className="rounded-xl border border-gray-200 p-4 mb-6" data-testid="lab-panel-highlights">
          {tier.highlightsIntro && <p className="text-sm font-semibold text-gray-900 mb-2">{tier.highlightsIntro}</p>}
          <ul className="space-y-1.5">
            {tier.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-2 h-2 mt-1.5 bg-[#2d8a5f] rounded-full flex-shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 mb-6 max-h-[420px] overflow-y-auto pr-1">
          {getCategoriesForSex(sex).map((cat) => (
            <div key={cat.key} className="rounded-xl border border-gray-200 p-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
                <span className="w-2 h-2 bg-[#2d8a5f] rounded-full" />
                {cat.label}
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">{cat.biomarkers.join(', ')}</p>
            </div>
          ))}
        </div>
      )}
      <button onClick={onNext} className="btn-primary" data-testid="continue-button">
        Order this panel — ${tier.price}
      </button>
    </div>
  );
};

// ?panel=125 / ?panel=160 preselects a tier (e.g. links from a panel page).
export const initialPanelAnswers = () => {
  try {
    const p = new URLSearchParams(window.location.search).get('panel');
    return p && LAB_PANEL_TIERS.some((t) => t.id === p) ? { 'panel-overview': p } : {};
  } catch (e) {
    return {};
  }
};

export default LabPanelOverview;
